import React from 'react';
import { Alert, Box, Paper, Typography } from '@mui/material';
import type { PatternDispositionSummary } from '../../domain/incidents/patternDispositionSummary';

export type PatternDispositionSummaryCardProps = {
  summary: PatternDispositionSummary | null;
  /** Same navigation as "Drill into observations" / review series queue. */
  onOpenReviewSeriesQueue?: () => void;
};

export const PatternDispositionSummaryCard: React.FC<PatternDispositionSummaryCardProps> = ({
  summary,
  onOpenReviewSeriesQueue,
}) => {
  if (!summary) return null;

  const clickable = typeof onOpenReviewSeriesQueue === 'function';

  return (
    <Paper
      variant="outlined"
      data-testid="pattern-disposition-summary"
      onClick={clickable ? onOpenReviewSeriesQueue : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenReviewSeriesQueue();
              }
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? 'Drill into observations' : undefined}
      sx={{
        p: 1.75,
        mb: 1.5,
        borderRadius: 2.5,
        borderColor: '#d0d7e2',
        bgcolor: '#ffffff',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
        '&:hover': clickable
          ? {
              borderColor: '#b8c2d1',
              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
            }
          : undefined,
      }}
    >
      <Typography
        sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}
      >
        {summary.historyTitle || 'PATTERN REVIEW HISTORY'}
      </Typography>
      {summary.historyLines?.length ? (
        <Box data-testid="pattern-review-history" sx={{ mt: 0.75 }}>
          {summary.historyLines.map((line) => (
            <Typography key={line} variant="body2" sx={{ fontWeight: 800, lineHeight: 1.55 }}>
              {line}
            </Typography>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.55, mt: 0.75 }}>
          {summary.headline}
        </Typography>
      )}
      {summary.verifiedNarrative ? (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55, mt: 0.65 }}>
          {summary.verifiedNarrative}
        </Typography>
      ) : null}
      {summary.coverageFact ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="pattern-disposition-coverage-fact"
          sx={{ lineHeight: 1.55, mt: 0.5 }}
        >
          {summary.coverageFact}
        </Typography>
      ) : null}
      {summary.highSeverityNudge ? (
        <Alert
          severity="info"
          data-testid="pattern-disposition-high-severity-nudge"
          sx={{ mt: 1.1, borderRadius: 2 }}
        >
          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
            {summary.highSeverityNudge}
          </Typography>
        </Alert>
      ) : null}
    </Paper>
  );
};
